import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { CostPriceAnalysis, EnrichedCostPriceAnalysis } from '../../services/costPrice/costPriceService';
import { Subrecipe } from '../../services/subrecipes/subrecipesService';
import { WasteEntry } from '../../services/waste/wasteService';
import { COST_PRICE_TYPE_CHOICE } from '../costPriceUtils';
import { productKey } from './shared';
import { explodeDishToProductNeeds } from './recipeExplosion';

/**
 * "Reported waste by product": the Phase-1 output of waste↔product linking. Turns the
 * period's waste entries into one row per canonical product key:
 *   - a DISH entry is exploded through its recipe tree (`explodeDishToProductNeeds`, the
 *     same walk the utilization report's "needed" column uses) × the portions thrown away;
 *   - a LINKED INGREDIENT entry contributes its own quantity directly.
 *
 * The two halves resolve their identity differently, on purpose:
 *   - INGREDIENTS are STORED links (`linked_platform`/`linked_article_id`/`linked_product_name`),
 *     because staff type them as free text and only a human or the AI matcher can say what was
 *     actually meant.
 *   - DISHES are DERIVED at read time by name (`buildDishMatchIndex`). Both the web and iOS
 *     dish flows pick from the revenue-item list — never free text — and a cost-price analysis
 *     carries that same name in `menuItemName`/`costPriceName`, so a near-miss is impossible by
 *     construction. Nothing is stored, so a renamed or newly set-up dish re-links itself.
 *
 * Pure and React-free, like the rest of `utils/reports/`. Quantities only — € valuation is
 * deliberately out of Phase 1 (order prices are factuur-overridden and price-adjusted on the
 * server read path; see `z_playbooks/dataRules/`).
 *
 * Unit rule: a product row carries ONE std base unit. A contribution measured in a different
 * unit is never summed into it — it is flagged instead, so a "500 ml" note against a product
 * bought by the gram can't silently inflate a gram total.
 */

/** Translation key per logged reason. Lives here rather than on the waste page because
 *  the utilization drawer renders the same reasons off the same rows. */
export const WASTE_REASON_LABELS: Record<string, string> = {
  expired: 'waste.reasonExpired',
  over_prep: 'waste.reasonOverPrep',
  mistake: 'waste.reasonMistake',
  bad_quality: 'waste.reasonBadQuality',
  accident: 'waste.reasonAccident',
  other: 'waste.reasonOther',
};

/** Human quantity for a logged waste line, AS LOGGED (g→kg, ml→L above 1000).
 *  Distinct from `fmtBase`, which formats an aggregated base-unit total. */
export function formatWasteQuantity(quantity: number, unit: string): string {
  if (unit === 'g' && quantity >= 1000) {
    return `${(quantity / 1000).toFixed(1).replace(/\.0$/, '')} kg`;
  }
  if (unit === 'ml' && quantity >= 1000) {
    return `${(quantity / 1000).toFixed(1).replace(/\.0$/, '')} L`;
  }
  if (unit === 'p') {
    return `${quantity} pcs`;
  }
  return `${quantity} ${unit}`;
}

/** The minimum an analysis must expose to be a dish-link target. */
export interface DishMatchable {
  id: string;
  costPriceName: string;
  menuItemName?: string;
  costPriceType?: string;
}

/** Lowercase, strip diacritics, collapse whitespace — the dish-name comparison key. */
export function normalizeDishName(name: string): string {
  return (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Name → cost-price analysis, for resolving a dish waste entry without storing a link.
 * Non-CHOICE analyses only, first writer wins within each pass.
 *
 * `menuItemName` WINS over `costPriceName`, and the two passes are what enforce it. A dish
 * waste name always comes from the revenue-item picker (`fetchRevenueItemNames` →
 * `/api/costprice/revenue-items/:restaurantId`, used by both the web and iOS log-waste
 * screens), and `menuItemName` is the field holding that POS link — `costPriceName` starts
 * equal to it but is a label the user can rename afterwards. Preferring `costPriceName`
 * would let a renamed dish shadow the analysis that actually owns the revenue item.
 *
 * Normalized (rather than `computeLoggedWaste`'s raw equality) so casing/spacing drift
 * between the revenue item and the analysis name doesn't strand an entry.
 */
export function buildDishMatchIndex<T extends DishMatchable>(analyses: T[]): Map<string, T> {
  const byName = new Map<string, T>();
  const relevant = analyses.filter((c) => c.costPriceType !== COST_PRICE_TYPE_CHOICE);
  // Pass 1 — the revenue-item link.
  for (const cpa of relevant) {
    const menu = normalizeDishName(cpa.menuItemName ?? '');
    if (menu && !byName.has(menu)) byName.set(menu, cpa);
  }
  // Pass 2 — the (renameable) display label, only where no revenue link claimed the name.
  for (const cpa of relevant) {
    const own = normalizeDishName(cpa.costPriceName);
    if (own && !byName.has(own)) byName.set(own, cpa);
  }
  return byName;
}

/** The analysis a dish waste entry resolves to, or undefined when none is set up yet. */
export function matchWasteDish<T extends DishMatchable>(itemName: string, index: Map<string, T>): T | undefined {
  return index.get(normalizeDishName(itemName));
}

/**
 * Whether an entry still needs a human. The two kinds need DIFFERENT actions, which is why
 * the page splits on `wasteType` after calling this: an unlinked ingredient needs LINKING,
 * while an unresolved dish needs its cost price SET UP (there is nothing to link it to yet).
 * A dish's stored `linkStatus` is ignored — dish identity is derived, never stored.
 */
export function needsWasteAttention<T extends DishMatchable>(entry: WasteEntry, dishIndex: Map<string, T>): boolean {
  if (entry.wasteType === 'dish') return !matchWasteDish(entry.itemName, dishIndex);
  return entry.linkStatus === 'unlinked';
}

/** Where a product's reported waste came from. */
export type WasteContributionSource = 'dish' | 'ingredient';

/** One reason's share of a product's reported waste. `reason` is `null` when unset. */
export interface WasteReasonShare {
  reason: string | null;
  quantity: number;
  entries: number;
}

/**
 * One waste ENTRY's contribution to one product row — the detail behind the totals,
 * so a consumer can show what was actually logged instead of only how much.
 *
 * One dish entry contributes to every product its recipe touches, so the same `wasteId`
 * legitimately appears once per product row (never twice within one row).
 */
export interface WasteContribution {
  wasteId: string;
  /** What the user logged: the dish name (picker) or the ingredient note (free text). */
  itemName: string;
  wasteType: 'dish' | 'ingredient';
  wasteDate: Date;
  reason: string | null;
  reasonNote: string | null;
  source: WasteContributionSource;
  /** As logged, BEFORE explosion: portions for a dish, qty+unit for an ingredient. */
  loggedQuantity: number;
  loggedUnit: string;
  /** This entry's share of the row, in the row's `stdUnit`. `null` when the entry's unit
   *  doesn't share a dimension with `stdUnit` — still listed so it stays visible, but
   *  never summed into `totalQuantity`. */
  quantity: number | null;
  /** Dish source only: the analysis the entry resolved to (for an "edit recipe" action). */
  dishName?: string;
  costPriceId?: string;
}

/** One product's reported waste over the period. */
export interface WasteByProductRow {
  /** Canonical `${platform}::${identifier}` key — the Phase-2 join key. */
  key: string;
  productName: string;
  platform: string;
  articleId?: string;
  /** Std base unit (g/ml/p) every summed quantity on this row is expressed in. */
  stdUnit: string;
  /** Total wasted base units (dish-exploded + ingredient), mismatched units excluded. */
  totalQuantity: number;
  fromDishQuantity: number;
  fromIngredientQuantity: number;
  /** Distinct waste entries that fed this row (incl. flagged ones). */
  entryCount: number;
  fromDishEntries: number;
  fromIngredientEntries: number;
  /** Reason split over the SUMMED quantity, biggest first. */
  reasons: WasteReasonShare[];
  /** Entries whose unit doesn't share a dimension with `stdUnit` — counted, never summed. */
  mismatchedEntries: number;
  /** The individual entries behind the totals, newest first. */
  contributions: WasteContribution[];
}

/** An entry that could not be rolled up, with the reason it was held out. */
export interface WasteLinkFlag {
  wasteId: string;
  itemName: string;
  /** `unit_mismatch` — entry unit ≠ the product's std base unit.
   *  `dish_no_match` — no cost-price analysis carries this dish's name yet (needs setup).
   *  `dish_no_products` — the dish's recipe resolves to no order-history products. */
  kind: 'unit_mismatch' | 'dish_no_match' | 'dish_no_products';
  /** Populated for `unit_mismatch`. */
  entryUnit?: string;
  productUnit?: string;
  productName?: string;
}

export interface WasteByProductResult {
  rows: WasteByProductRow[];
  /** Entries in the period that produced at least one contribution. */
  linkedEntries: number;
  /** Entries in the period still needing a human: unlinked ingredients + unmatched dishes. */
  unlinkedEntries: number;
  flags: WasteLinkFlag[];
}

/** Stable empty result, so a caller that isn't showing the view can skip the work
 *  without handing downstream code a fresh object (or a half-computed one) each render. */
export const EMPTY_WASTE_BY_PRODUCT: WasteByProductResult = {
  rows: [],
  linkedEntries: 0,
  unlinkedEntries: 0,
  flags: [],
};

export interface WasteByProductInputs {
  /** Waste entries ALREADY filtered to the selected period by the caller. */
  wasteEntries: WasteEntry[];
  /** Order history — supplies each linked product's std base unit + display name. */
  orders: OrderHistory[];
  /** Enriched cost prices, so a linked dish resolves to its recipe. */
  enriched: EnrichedCostPriceAnalysis[];
  subrecipesById: Map<string, Subrecipe>;
  defaultWastePercentage: number;
  currentRestaurantId: string;
}

/** Display identity + std unit per product key, harvested from order history. */
interface OrderProductInfo {
  name: string;
  platform: string;
  articleId?: string;
  stdUnit: string;
}

function collectOrderProductInfo(orders: OrderHistory[]): Map<string, OrderProductInfo> {
  const infos = new Map<string, OrderProductInfo>();
  for (const o of orders) {
    for (const p of o.productData) {
      const key = productKey(o.platform, p.articleId, p.name);
      const existing = infos.get(key);
      if (!existing) {
        infos.set(key, {
          name: p.name,
          platform: o.platform,
          articleId: p.articleId,
          stdUnit: p.quantityInfo?.stdUnit ?? '',
        });
      } else if (!existing.stdUnit && p.quantityInfo?.stdUnit) {
        // A later line may carry the quantityInfo an earlier one lacked.
        existing.stdUnit = p.quantityInfo.stdUnit;
      }
    }
  }
  return infos;
}

/** One product's accumulating totals while entries are folded in. */
interface RowAccumulator {
  row: WasteByProductRow;
  reasons: Map<string, { reason: string | null; quantity: number; entries: number }>;
  entryIds: Set<string>;
}

/**
 * Waste units (`g`/`ml`/`p`) are already the std base units the report keys on, so
 * "shares a dimension" is literal equality. An unknown product unit (product never seen
 * in order history, or bought without quantity info) can't contradict anything — the
 * row simply adopts the entry's unit.
 */
function unitsCompatible(entryUnit: string, productUnit: string): boolean {
  return productUnit === '' || entryUnit === '' || entryUnit === productUnit;
}

export function computeWasteByProduct(inputs: WasteByProductInputs): WasteByProductResult {
  const { wasteEntries, orders, enriched, subrecipesById, defaultWastePercentage, currentRestaurantId } = inputs;

  const orderInfo = collectOrderProductInfo(orders);
  // Dish identity is derived from the entry's name, never stored — see the file header.
  // `costPriceById` is separate: it lets the walk resolve nested cost-price references.
  const costPriceById = new Map<string, CostPriceAnalysis>(enriched.map((c) => [c.id, c]));
  const dishIndex = buildDishMatchIndex(enriched);

  const accumulators = new Map<string, RowAccumulator>();
  const flags: WasteLinkFlag[] = [];
  let linkedEntries = 0;
  let unlinkedEntries = 0;

  const contribute = (args: {
    key: string;
    name: string;
    platform: string;
    articleId?: string;
    unit: string;
    quantity: number;
    source: WasteContributionSource;
    entry: WasteEntry;
    /** Dish source only — the analysis the entry resolved to. */
    dishName?: string;
    costPriceId?: string;
  }): void => {
    let acc = accumulators.get(args.key);
    if (!acc) {
      const info = orderInfo.get(args.key);
      acc = {
        row: {
          key: args.key,
          productName: info?.name || args.name || args.key,
          platform: info?.platform || args.platform,
          articleId: info?.articleId ?? args.articleId,
          // First contribution decides the row's unit; the product's own std unit wins
          // when order history knows it.
          stdUnit: info?.stdUnit || args.unit,
          totalQuantity: 0,
          fromDishQuantity: 0,
          fromIngredientQuantity: 0,
          entryCount: 0,
          fromDishEntries: 0,
          fromIngredientEntries: 0,
          reasons: [],
          mismatchedEntries: 0,
          contributions: [],
        },
        reasons: new Map(),
        entryIds: new Set(),
      };
      accumulators.set(args.key, acc);
    }

    const isNewEntry = !acc.entryIds.has(args.entry.id);
    if (isNewEntry) {
      acc.entryIds.add(args.entry.id);
      acc.row.entryCount += 1;
      if (args.source === 'dish') acc.row.fromDishEntries += 1;
      else acc.row.fromIngredientEntries += 1;
    }

    /** The entry-level record, `quantity: null` when it was held out of the totals. */
    const record = (quantity: number | null): WasteContribution => ({
      wasteId: args.entry.id,
      itemName: args.entry.itemName,
      wasteType: args.entry.wasteType,
      wasteDate: args.entry.wasteDate,
      reason: args.entry.reason,
      reasonNote: args.entry.reasonNote,
      source: args.source,
      loggedQuantity: args.entry.quantity,
      loggedUnit: args.entry.unit,
      quantity,
      dishName: args.dishName,
      costPriceId: args.costPriceId,
    });

    if (!unitsCompatible(args.unit, acc.row.stdUnit)) {
      acc.row.mismatchedEntries += 1;
      acc.row.contributions.push(record(null));
      flags.push({
        wasteId: args.entry.id,
        itemName: args.entry.itemName,
        kind: 'unit_mismatch',
        entryUnit: args.unit,
        productUnit: acc.row.stdUnit,
        productName: acc.row.productName,
      });
      return;
    }

    acc.row.contributions.push(record(args.quantity));

    acc.row.totalQuantity += args.quantity;
    if (args.source === 'dish') acc.row.fromDishQuantity += args.quantity;
    else acc.row.fromIngredientQuantity += args.quantity;

    const reasonKey = args.entry.reason ?? '';
    const cur = acc.reasons.get(reasonKey);
    if (cur) {
      cur.quantity += args.quantity;
      if (isNewEntry) cur.entries += 1;
    } else {
      acc.reasons.set(reasonKey, { reason: args.entry.reason, quantity: args.quantity, entries: 1 });
    }
  };

  for (const entry of wasteEntries) {
    if (entry.wasteType === 'dish') {
      // Derived, not stored: resolve the analysis by name every time.
      const dish = matchWasteDish(entry.itemName, dishIndex);
      if (!dish) {
        unlinkedEntries += 1;
        flags.push({ wasteId: entry.id, itemName: entry.itemName, kind: 'dish_no_match' });
        continue;
      }
      const needs = explodeDishToProductNeeds(dish, subrecipesById, {
        defaultWastePercentage,
        restaurantId: currentRestaurantId,
        costPriceById,
      });
      if (needs.length === 0) {
        flags.push({ wasteId: entry.id, itemName: entry.itemName, kind: 'dish_no_products' });
        continue;
      }
      linkedEntries += 1;
      // A dish entry's `quantity` is portions thrown away (the convention `computeLoggedWaste`
      // already values dish waste with), whatever unit the logger picked.
      for (const need of needs) {
        contribute({
          key: need.productKey,
          name: need.productName,
          platform: need.platform,
          articleId: need.articleId,
          unit: need.stdUnit,
          quantity: need.perDishBase * entry.quantity,
          source: 'dish',
          entry,
          dishName: dish.costPriceName,
          costPriceId: dish.id,
        });
      }
      continue;
    }

    // Ingredients stay stored links: only a human or the matcher can resolve free text.
    if (entry.linkStatus !== 'linked') {
      if (entry.linkStatus === 'unlinked') unlinkedEntries += 1;
      continue;
    }
    if (!entry.linkedPlatform) {
      // Marked linked without an ingredient identity — nothing to attribute.
      continue;
    }
    linkedEntries += 1;
    contribute({
      key: productKey(entry.linkedPlatform, entry.linkedArticleId ?? undefined, entry.linkedProductName ?? undefined),
      name: entry.linkedProductName || entry.itemName,
      platform: entry.linkedPlatform,
      articleId: entry.linkedArticleId ?? undefined,
      unit: entry.unit,
      quantity: entry.quantity,
      source: 'ingredient',
      entry,
    });
  }

  const rows = Array.from(accumulators.values()).map((acc) => ({
    ...acc.row,
    reasons: Array.from(acc.reasons.values()).sort((a, b) => b.quantity - a.quantity),
    contributions: acc.row.contributions.sort(
      (a, b) => new Date(b.wasteDate).getTime() - new Date(a.wasteDate).getTime() || a.itemName.localeCompare(b.itemName)
    ),
  }));
  rows.sort((a, b) => b.totalQuantity - a.totalQuantity || a.productName.localeCompare(b.productName));

  return { rows, linkedEntries, unlinkedEntries, flags };
}
